/**
 * NOTE: This file is used by both the browser and NodeJS tooling code of this
 *       repo. Please keep this file free of code tying it to either
 *       environment.
 *
 * NOTE: We rely on the `setupEnvBeforeDotEnv` function (search for it) that
 *       makes sure that these env vars are available for all these functions
 *       to work.
 */

import {
  Env,
  getEnvVar,
  getJsonEnvVar,
  ensureCanAccessSharedFabricCore,
} from "./env.private";

import { getIsRunningInContainer } from "./env.private";
export { getAppName, getIsRunningInContainer } from "./env.private";

export type { Env };
export type DepsMap = Record<string, string>;

export const envVarToBool = (s: string | undefined): boolean =>
  !!(s && s.toLowerCase() !== "false");

export const getAppGitCommitHash = (env: Env): string => {
  return getEnvVar(env, "REACT_APP_GIT_COMMIT_HASH");
};

// TODO: This should be updated to return `string`
// instead of `string | undefined`, once all apps are on current tooling.
export const getAppPackageJsonVersion = (env: Env): string | undefined => {
  try {
    return getEnvVar(env, "REACT_APP_PACKAGE_JSON_VERSION");
  } catch (e) {
    return undefined;
  }
};

export const getAppPackageJsonDeps = (env: Env): DepsMap => {
  return getJsonEnvVar(env, "REACT_APP_PACKAGE_JSON_DEPS");
};

export const getAppPackageJsonDevDeps = (env: Env): DepsMap => {
  return getJsonEnvVar(env, "REACT_APP_PACKAGE_JSON_DEV_DEPS");
};

export const getAppActualDepVersions = (env: Env): DepsMap => {
  return getJsonEnvVar(env, "REACT_APP_ACTUAL_DEP_VERSIONS");
};

export const getAppFabricCoreSharedSingleDepNames = (env: Env): string[] => {
  return getJsonEnvVar(env, "REACT_APP_SHARED_SINGLE_DEP_NAMES");
};

export const getAppFabricCoreSharedFlexDepNames = (env: Env): string[] => {
  return getJsonEnvVar(env, "REACT_APP_SHARED_FLEX_DEP_NAMES");
};

export const getAppFabricCoreAllSharedDepNames = (env: Env): string[] => {
  return [
    ...getAppFabricCoreSharedSingleDepNames(env),
    ...getAppFabricCoreSharedFlexDepNames(env),
  ];
};

export const getSharedFabricCoreSharedSingleDepNames = (): string[] => {
  ensureCanAccessSharedFabricCore("getSharedFabricCoreSharedSingleDepNames");
  return getJsonEnvVar(process.env, "REACT_APP_SHARED_SINGLE_DEP_NAMES");
};

export const getSharedFabricCoreSharedFlexDepNames = (): string[] => {
  ensureCanAccessSharedFabricCore("getSharedFabricCoreSharedFlexDepNames");
  return getJsonEnvVar(process.env, "REACT_APP_SHARED_FLEX_DEP_NAMES");
};

export const getSharedFabricCorePackageJsonVersion = (): string => {
  ensureCanAccessSharedFabricCore("getSharedFabricCorePackageJsonVersion");
  return getEnvVar(
    process.env,
    "REACT_APP_SHARED_FABRIC_CORE_PACKAGE_JSON_VERSION",
  );
};

export const getSharedFabricCorePackageJsonDeps = (): DepsMap => {
  ensureCanAccessSharedFabricCore("getSharedFabricCorePackageJsonDeps");
  return getJsonEnvVar(
    process.env,
    "REACT_APP_SHARED_FABRIC_CORE_PACKAGE_JSON_DEPS",
  );
};

export const getIsInStandaloneMode = (): boolean => {
  return !getIsRunningInContainer();
};

export const getProxiedContainerHostname = (): string | undefined => {
  return window["FABRIC_PROXIED_CONTAINER_HOSTNAME"] || undefined;
};
